import styles from 'css/src/components/public-pages/ContentfulPage.module.scss';

import React from 'react';

import JumboCta from 'js/src/components/common/JumboCta';
import AudioSyncUtilityComponent from 'js/src/components/public-pages/contentful-components/AudioSyncUtilityComponent';
import CaseStudyComponent from 'js/src/components/public-pages/contentful-components/CaseStudyComponent';
import CenteredMediaComponent from 'js/src/components/public-pages/contentful-components/CenteredMediaComponent';
import FAQComponent from 'js/src/components/public-pages/contentful-components/FAQComponent';
import ScrollingLogosComponent from 'js/src/components/public-pages/contentful-components/ScrollingLogosComponent';
import ThirdPartyScriptComponent from 'js/src/components/public-pages/contentful-components/ThirdPartyScriptComponent';
import Hero from 'js/src/components/public-pages/Hero';
import ImageAndText from 'js/src/components/public-pages/ImageAndText';
import PageListing from 'js/src/components/public-pages/PageListing';
import SixIcons from 'js/src/components/public-pages/SixIcons';
import ThreeColumnText from 'js/src/components/public-pages/ThreeColumnText';
import Title from 'js/src/components/public-pages/Title';
import TripleCard from 'js/src/components/public-pages/TripleCard';
import type { DeepReadonly } from 'js/src/libs/consts';
import type { ContentfulComponentData, ContentfulPageType } from 'js/src/libs/contentful-utils';

const COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
    heroComponent: Hero,
    sixIconsComponent: SixIcons,
    imageAndTextComponent: ImageAndText,
    threeColumnTextComponent: ThreeColumnText,
    titleComponent: Title,
    tripleCardComponent: TripleCard,
    pageListingComponent: PageListing,
    thirdPartyScriptComponent: ThirdPartyScriptComponent,
    centeredMediaComponent: CenteredMediaComponent,
    scrollingLogosComponent: ScrollingLogosComponent,
    faqComponent: FAQComponent,
    caseStudyComponent: CaseStudyComponent,
    audioSyncUtilityComponent: AudioSyncUtilityComponent,
};

const WHITE = '#FFF';
const LIGHT_GREY = '#FAFAFA';

const getBgColorFor = (
    componentList: ReadonlyArray<DeepReadonly<ContentfulComponentData>>,
    index: number,
): string => {
    // The title should have the same bg color as the content below,
    // The scrolling logos should have the same bg color as the content above,
    // other than that, the background color should be toggled for every
    // component.
    const currentComponent = componentList[index];
    const CurrentComponentClass = COMPONENT_MAP[currentComponent?._content_type];
    const isCurrentScrollingLogos = CurrentComponentClass === ScrollingLogosComponent;

    // If current component is scrolling logos, get the color from the previous component
    if (isCurrentScrollingLogos && index > 0) {
        return getBgColorFor(componentList, index - 1);
    }

    let bgColor = WHITE;
    for (let i = 0; i < index; i++) {
        const component = componentList[i];
        const ComponentClass = COMPONENT_MAP[component?._content_type];
        const isTitle = ComponentClass === Title;
        const isScrollingLogos = ComponentClass === ScrollingLogosComponent;
        // Skip Title and ScrollingLogos when toggling - they don't affect the alternation
        if (!isTitle && !isScrollingLogos) {
            bgColor = bgColor === WHITE ? LIGHT_GREY : WHITE;
        }
    }
    return bgColor;
};

type Props = {
    data: DeepReadonly<ContentfulPageType>;
};

const ContentfulPage = React.memo(function ContentfulPage({ data }: Props) {
    const components = data.content_components;
    const componentsList = components.map((componentData, index) => {
        const ComponentClass = COMPONENT_MAP[componentData._content_type];
        const isBelowTheFold = index > 1; // the first 2 components are visible on page load
        return (
            <ComponentClass
                data={componentData}
                bgColor={getBgColorFor(components, index)}
                key={componentData.id}
                lazyLoad={isBelowTheFold}
            />
        );
    });

    return (
        <div className={styles['contentful-page']}>
            {componentsList}
            <div className="mb-l5-48">
                <JumboCta
                    header="Join over 5,000,000 companies using Lumen5 to tell their stories."
                    body="Lumen5 is a video creation software that helps marketers, publishers, and brands create video content in a breeze, without any technical expertise."
                />
            </div>
        </div>
    );
});

export default ContentfulPage;
